'use strict';

import React, { useEffect, useState } from 'react';
import {
    DotRouter,
    DotRouterTab,
} from '@hms-dbmi-bgm/shared-portal-components/es/components/ui/DotRouter';
import { ajax } from '@hms-dbmi-bgm/shared-portal-components/es/components/util';

import { FileOverviewTableController } from './FileOverviewTable';
import { VcfAnalysisOverview } from './VcfAnalysisOverview';
import { QcOverviewTabContent } from './QcOverviewTabContent';
import ReactTooltip from 'react-tooltip';
import { useUserDownloadAccess } from '../../../util/hooks';

/**
 * DotRouterTab content for displaying the files in the same file set as the
 * current file. Separates the files into those generated by the DAC and those
 * generated externally.
 */
const AssociatedFilesTab = (props) => {
    const { context = {} } = props;
    const fileSets = context?.file_sets || [];

    // Create a search href for retrieving BAM/CRAM files in the same file set
    let DACGeneratedFilesHref, ExternallyGeneratedFilesHref;

    if (fileSets.length > 0) {
        const fileSetUuids = context?.file_sets
            ?.map((fs) => fs.uuid)
            ?.join('&file_sets.uuid=');
        const associatedFilesSearchHref = `/search/?type=File&uuid!=${props.context.uuid}&file_sets.uuid=${fileSetUuids}`;

        DACGeneratedFilesHref =
            associatedFilesSearchHref +
            '&status=open&status=open-early&status=open-network&status=protected&status=protected-early&status=protected-network&submission_centers.display_title=HMS DAC';

        ExternallyGeneratedFilesHref =
            associatedFilesSearchHref +
            '&file_format.display_title=cram&file_format.display_title=bam&submission_centers.display_title!=HMS DAC';
    }

    return (
        <div className="content associated-files">
            {fileSets.length > 0 ? (
                <>
                    <FileOverviewTableController
                        {...props}
                        embeddedTableHeaderText="DAC Generated Files"
                        associatedFilesSearchHref={DACGeneratedFilesHref}
                    />
                    <FileOverviewTableController
                        {...props}
                        embeddedTableHeaderText="Externally Generated Files"
                        associatedFilesSearchHref={ExternallyGeneratedFilesHref}
                    />
                </>
            ) : (
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="icon icon-folder-open fas"></i>
                        <h3 className="header">No Associated Files Found</h3>
                        <span className="subheader">
                            There are no DAC generated or externally generated
                            files associated with this file
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

// DotRouterTab content for displaying Analysis information for the current file.
const AnalysisInformationTab = (props) => {
    if (
        props?.context?.file_summary?.file_format === 'vcf' ||
        props?.context?.file_summary?.file_format === 'vcf_gz'
    ) {
        return <VcfAnalysisOverview {...props} />;
    } else {
        // no results
        return (
            <div className="no-results">
                <div className="no-results-content">
                    <i className="icon icon-network-wired fas"></i>
                    <h3 className="header">Analysis Information Coming Soon</h3>
                    <span className="subheader">
                        Check back for updates on Analysis Information
                        development with future portal releases
                    </span>
                </div>
            </div>
        );
    }
};

// DotRouterTab content for displaying QC information for the current file.
const QCOverviewTab = ({ session, context }) => {
    // Check if quality metrics exist and are valid
    const fileHasQualityMetrics =
        context?.quality_metrics && context?.quality_metrics?.length > 0;

    // Check if user has permission to view quality metrics
    const userHasPermission = !context?.quality_metrics?.[0]?.error;

    // If quality metrics exist and user has access, show QCOverviewTabContent
    // Otherwise, show appropriate message
    if (fileHasQualityMetrics) {
        return userHasPermission ? (
            <QcOverviewTabContent session={session} context={context} />
        ) : (
            <div className="protected-data callout-card">
                <i className="icon icon-user-lock fas"></i>
                <h4>Protected Data</h4>
                <span>
                    To view this data, you must have access
                    <br /> to SMaHT protected access data on dbGaP.
                </span>
            </div>
        );
    } else {
        return (
            <div className="no-results">
                <div className="no-results-content">
                    <i className="icon icon-chart-area fas"></i>
                    <h3 className="header">QC Overview Coming Soon</h3>
                    <span className="subheader">
                        Check back for updates on QC Overview development with
                        future portal releases
                    </span>
                </div>
            </div>
        );
    }
};

export const FileViewTabs = (props) => {
    const { context } = props;
    const { file_sets = [], uuid } = context || {};

    const [associatedFilesTitle, setAssociatedFilesTitle] =
        useState('Associated Files');

    useEffect(() => {
        let fileset_uuid_query_params = '';
        file_sets.forEach((file_set) => {
            fileset_uuid_query_params += '&file_sets.uuid=' + file_set.uuid;
        });

        const searchURL =
            `/search/?type=File&uuid!=${uuid}&status=retracted&status=obsolete&notes_to_tsv!=No+value` +
            fileset_uuid_query_params;

        ajax.load(
            searchURL,
            (resp) => {
                setAssociatedFilesTitle(
                    <div data-tip="Some associated files may have status 'redacted' or 'obsolete'. Please check for notes before downloading.">
                        <i className="icon fas icon-exclamation-triangle text-warning me-05"></i>
                        Associated Files
                    </div>
                );
                ReactTooltip.rebuild();
            },
            'GET',
            (err) => {
                if (err.notification !== 'No results found') {
                    console.log('ERROR FileViewTabs resp', err);
                }
            }
        );
    }, []);

    return (
        <div className="tabs-container">
            <DotRouter
                href={props.href}
                navClassName=""
                isActive={true}
                prependDotPath="file-overview">
                <DotRouterTab
                    dotPath=".analysis-information"
                    tabTitle="Analysis Information"
                    arrowTabs={false}
                    cache={true}
                    default>
                    <AnalysisInformationTab {...props} />
                </DotRouterTab>
                <DotRouterTab
                    dotPath=".qc-overview"
                    tabTitle="QC Overview"
                    arrowTabs={false}
                    cache={true}>
                    <QCOverviewTab {...props} />
                </DotRouterTab>
                <DotRouterTab
                    dotPath=".associated-files"
                    tabTitle="Associated Files"
                    arrowTabs={false}
                    cache={true}>
                    <AssociatedFilesTab {...props} />
                </DotRouterTab>
            </DotRouter>
        </div>
    );
};
